// Chat API Service - Backend Integration
import axios from 'axios';
import { getCurrentUser } from './authService';
import { apiGet, apiPut } from '../services/apiClient';
import toast from 'react-hot-toast';

/**
 * Get BASE_URL with automatic environment detection (same logic as apiClient.js)
 * Priority:
 * 1. Auto-detect from window.location.hostname (dynamic)
 * 2. Environment variable fallback
 * 3. Default to localhost for development
 */
const getBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol; // 'http:' or 'https:'
  
  // Local development detection
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:8080';
  } else {
    // Production: Use same protocol and hostname as frontend
    return `${protocol}//${hostname}`;
  }
};

const BASE_URL = getBaseUrl();

// Notification service base URL (hd-notification service)
// For REST API, use gateway. For WebSocket, connect directly to service port 8085
// Use gateway route: /gateway/hd-notification
const NOTIFICATION_SERVICE_URL = process.env.REACT_APP_NOTIFICATION_SERVICE_URL || 
                                 process.env.REACT_APP_NOTIFICATION_SERVICE_DIRECT_URL ||
                                 `${BASE_URL}/gateway/hd-notification`;

// Create axios instance for chat API
const CHAT_API = axios.create({
  baseURL: NOTIFICATION_SERVICE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication interceptor
CHAT_API.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
CHAT_API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      //console.log('🔄 401 Unauthorized - Chat API');
      toast.error(error.response?.data?.message || 'Your session has been terminated as your account was logged in from another device.');
      setTimeout(() => {
        window.location.href = '/signin';
      }, 3000);
    }
    return Promise.reject(error);
  });

/**
 * Initiate a new chat session (Guest - No auth required)
 * @param {Object} request - { guestName, email?, subject?, ticketNumber? }
 * @returns {Promise} Response with chatSessionId and websocketUrl
 */
export const initiateChat = async (request) => {
  try {
    const response = await CHAT_API.post('/api/public/chat/initiate', request);
    return response.data;
  } catch (error) {
    console.error('Error initiating chat:', error);
    throw error;
  }
};

/**
 * Accept a chat session (Agent - Requires auth)
 * @param {string} chatSessionId - The chat session ID to accept
 * @returns {Promise} Response with accepted chat session details
 */
export const acceptChat = async (chatSessionId) => {
  try {
    return await apiPut(`/hd-notification/api/chat/accept/${chatSessionId}`, {});
  } catch (error) {
    console.error('Error accepting chat:', error);
    throw error;
  }
};

/**
 * Pending chat sessions waiting for an agent (GET /api/chat/pending-requests).
 * @returns {Promise<Array>} list of pending session rows (normalized array; empty on failure)
 */
export const fetchPendingChatRequests = async () => {
  const body = await apiGet('/hd-notification/api/chat/pending-requests');
  if (body && body.success === false) {
    throw new Error(body.message || 'Failed to load pending chat requests');
  }
  const data = body?.data ?? body;
  return Array.isArray(data) ? data : [];
};

/**
 * Get chat session details
 * @param {string} chatSessionId - The chat session ID
 * @returns {Promise} Chat session details
 */
export const getChatSession = async (chatSessionId) => {
  try {
    const response = await CHAT_API.get(`/api/chat/${chatSessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching chat session:', error);
    throw error;
  }
};

/**
 * End/Close a chat session (Agent - Requires auth)
 * @param {string} chatSessionId - The chat session ID to end
 * @returns {Promise} Response with success message
 */
export const endChat = async (chatSessionId) => {
  try {
    const response = await CHAT_API.put(`/api/chat/end/${chatSessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error ending chat:', error);
    throw error;
  }
};

export default CHAT_API;
